import { MouseEvent, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { useDialog } from "components/Actions/ActionBar/hooks";
import {
    COMPANY_ACTIONS,
    DEVICE_ACTIONS,
    DEVICE_MODEL_ACTIONS,
    FILE_ACTIONS,
    GROUP_ACTIONS,
    PRODUCT_CODE_ACTIONS,
    ROLE_ACTIONS,
    TASK_ACTIONS,
    USER_ACTIONS
} from "components/Actions/types";
import TableContext from "context/table/tableContext";
import { MenuAnchorEl, TABLE_NAMES, usePermissions } from "shared";

const useActions = (toggleActiveHead?: (value: boolean) => void) => {
    const { outerDetailsData, tableName, deselectAllRows } =
        useContext(TableContext);

    const { t } = useTranslation();
    const { isOpenDialog, openDialog, closeDialog } = useDialog();
    const { PERMISSIONS, getNoPermissionTooltipTitle } = usePermissions();

    const [anchorEl, setAnchorEl] = useState<MenuAnchorEl>(null);

    const handleCloseMenu = () => setAnchorEl(null);

    const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>) =>
        setAnchorEl(e.currentTarget);

    const resetView = () => {
        toggleActiveHead?.(false);
        deselectAllRows();
    };

    const actions: Partial<
        Record<
            TABLE_NAMES,
            | COMPANY_ACTIONS
            | TASK_ACTIONS
            | FILE_ACTIONS
            | GROUP_ACTIONS
            | USER_ACTIONS
            | PRODUCT_CODE_ACTIONS
            | DEVICE_MODEL_ACTIONS
            | DEVICE_ACTIONS
            | ROLE_ACTIONS
        >
    > = {
        [TABLE_NAMES.Tasks]: TASK_ACTIONS.CancelTasks,
        [TABLE_NAMES.DeviceGroups]: GROUP_ACTIONS.DeleteDeviceGroups,
        [TABLE_NAMES.Files]: FILE_ACTIONS.DeleteFiles,
        [TABLE_NAMES.Companies]: COMPANY_ACTIONS.DeleteCompanies,
        [TABLE_NAMES.Users]: USER_ACTIONS.DeleteUsers,
        [TABLE_NAMES.ProductCodes]: PRODUCT_CODE_ACTIONS.DeleteProductCode,
        [TABLE_NAMES.DeviceModels]: DEVICE_MODEL_ACTIONS.DeleteModels,
        [TABLE_NAMES.Roles]: ROLE_ACTIONS.DeleteRoles
    };

    const action = actions[tableName];

    const isCancelTasksAction = action === TASK_ACTIONS.CancelTasks;

    const buttonText = isCancelTasksAction
        ? t("Button##cancel tasks")
        : t("Button##delete");

    const noPermissionTooltipTitle = () => {
        switch (action) {
            case TASK_ACTIONS.CancelTasks:
                return getNoPermissionTooltipTitle(PERMISSIONS.TaskUpdate);
            case GROUP_ACTIONS.DeleteDeviceGroups:
                return getNoPermissionTooltipTitle(
                    PERMISSIONS.DeviceGroupDelete
                );
            case FILE_ACTIONS.DeleteFiles:
                return getNoPermissionTooltipTitle(PERMISSIONS.FileDelete);
            case COMPANY_ACTIONS.DeleteCompanies:
                return getNoPermissionTooltipTitle(PERMISSIONS.CompanyDelete);
            case USER_ACTIONS.DeleteUsers:
                return getNoPermissionTooltipTitle(PERMISSIONS.UserDelete);
            case ROLE_ACTIONS.DeleteRoles:
                return getNoPermissionTooltipTitle(PERMISSIONS.RoleDelete);
            default:
                return undefined;
        }
    };

    const isDisabled = noPermissionTooltipTitle() !== undefined;
    const mainTableName = outerDetailsData?.mainTableName;

    return {
        action,
        anchorEl,
        buttonText,
        isCancelTasksAction,
        isDisabled,
        isOpenDialog,
        mainTableName,
        handleCloseMenu,
        handleOpenMenu,
        noPermissionTooltipTitle,
        closeDialog,
        openDialog,
        resetView
    };
};

export { useActions };
